import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import { AlertTriangle } from "lucide-react";
import { buildHubSpotContactUrl, HUBSPOT_ORANGE } from "../utils/hubspot";

/**
 * The sprocket cell (ADR-169 Stage 2, review-first per ADR-172) — a
 * list row's HubSpot interface and status light:
 *
 *  - HubSpot-orange sprocket = linked. A plain link straight to the
 *    contact record (middle-click and cmd-click work).
 *  - Gray sprocket = unlinked. Clicking opens the page-owned match
 *    dialog (`HubSpotMatchDialog`) — the organizer picks a candidate
 *    there and reviews the pulled data in the person's edit form.
 *    Nothing links or writes from the click itself.
 *
 * Color is never the sole signal — tooltip and aria-label differ by
 * state.
 */

interface HubSpotLinkCellProps {
  personId: string;
  email: string;
  displayName: string;
  /** The linked contact's record ID; null/undefined = unlinked. */
  contactId?: string | null;
  /** Gray-state click — open the match dialog for this person. */
  onOpenMatch: () => void;
  /** Link-map state from the page-level personInternals query. */
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function HubSpotLinkCell({
  personId,
  email,
  displayName,
  contactId,
  onOpenMatch,
  loading = false,
  error = null,
  onRetry,
}: HubSpotLinkCellProps) {
  if (!email) return null;

  if (loading) {
    return (
      <CircularProgress
        size={16}
        aria-label={`Loading HubSpot link status for ${displayName}`}
        data-testid={`loading-hubspot-${personId}`}
      />
    );
  }

  if (error) {
    return (
      <Tooltip title="HubSpot link status unavailable — click to retry">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRetry?.();
          }}
          disabled={!onRetry}
          color="warning"
          aria-label={`Retry HubSpot link status for ${displayName}`}
          data-testid={`retry-hubspot-${personId}`}
        >
          <AlertTriangle size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  if (contactId) {
    const url = buildHubSpotContactUrl({ email, contactId });
    if (!url) return null;
    return (
      <Tooltip title={`Open ${displayName} in HubSpot`}>
        <IconButton
          size="small"
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{ color: HUBSPOT_ORANGE }}
          aria-label={`Open ${displayName} in HubSpot (linked)`}
          data-testid={`open-hubspot-${personId}`}
        >
          <FontAwesomeIcon icon={faHubspot} style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Not linked — find ${displayName} in HubSpot`}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onOpenMatch();
        }}
        aria-label={`Not linked — find ${displayName} in HubSpot`}
        data-testid={`link-hubspot-${personId}`}
      >
        <FontAwesomeIcon icon={faHubspot} style={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}
